import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { Button } from '../components/ui/button';
import { ArrowRight, Clock, Users, Calendar, Monitor, Award, BarChart3, Check } from 'lucide-react';
import { motion } from 'framer-motion';

export default function HowItWorksPage() {
  const { t, language } = useLanguage();

  const keyFacts = language === 'fr' ? [
    { icon: Clock, value: '2 x 45 min', label: 'par semaine', desc: 'Après les cours, le mercredi ou le samedi' },
    { icon: Users, value: '4-8 élèves', label: 'par classe', desc: 'Enseignement personnalisé en petit groupe' },
    { icon: Award, value: 'Professeurs', label: 'natifs et certifiés', desc: 'Enseignants qualifiés, diplômés américains ou britanniques' },
    { icon: Calendar, value: '32 semaines', label: 'par année scolaire', desc: 'Un programme complet et structuré' },
    { icon: Monitor, value: '100% en ligne', label: 'en direct', desc: 'Depuis la maison, sur notre plateforme dédiée' },
  ] : [
    { icon: Clock, value: '2 x 45 min', label: 'per week', desc: 'After school, on Wednesdays or Saturdays' },
    { icon: Users, value: '4-8 students', label: 'per class', desc: 'Personalized small group instruction' },
    { icon: Award, value: 'Native', label: 'certified teachers', desc: 'Qualified teachers with American or British degrees' },
    { icon: Calendar, value: '32 weeks', label: 'per school year', desc: 'A complete and structured program' },
    { icon: Monitor, value: '100% online', label: 'live classes', desc: 'From home, on our dedicated platform' },
  ];

  const steps = language === 'fr' ? [
    { num: '1', title: 'Le matin et la journée', desc: 'Votre enfant suit sa scolarité française normalement dans son établissement.' },
    { num: '2', title: 'Après les cours ou le mercredi/samedi', desc: 'Il rejoint sa classe OFA France en ligne pour 45 minutes de cours en direct avec son professeur et ses camarades.' },
    { num: '3', title: 'Entre les cours', desc: 'Des activités asynchrones permettent de consolider les apprentissages à son rythme.' },
    { num: '4', title: 'Tout au long de l\'année', desc: 'Bulletins trimestriels (progress reports) et semestriels envoyés aux parents via notre SIS. Communication régulière avec les familles.' },
  ] : [
    { num: '1', title: 'Morning and daytime', desc: 'Your child attends their French school as normal.' },
    { num: '2', title: 'After school or Wednesday/Saturday', desc: 'They join their OFA France class online for 45 minutes of live instruction with their teacher and classmates.' },
    { num: '3', title: 'Between classes', desc: 'Asynchronous activities allow students to consolidate learning at their own pace.' },
    { num: '4', title: 'Throughout the year', desc: 'Quarterly progress reports and semester report cards sent to parents via our SIS. Regular communication with families.' },
  ];

  const profilePoints = [
    t.howItWorks.profile.point1,
    t.howItWorks.profile.point2,
    t.howItWorks.profile.point3,
    t.howItWorks.profile.point4,
  ];

  const profileConclusion = language === 'fr'
    ? 'Un parcours conçu comme un véritable atout pour l\'avenir académique, international et professionnel des élèves.'
    : 'A pathway designed as a genuine asset for students\' academic, international and professional futures.';

  return (
    <div data-testid="how-it-works-page" className="min-h-screen bg-white">
      <SEO path="/how-it-works" />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#14274B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.howItWorks.heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/70 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr'
              ? 'Un programme 100% en ligne, complémentaire à la scolarité française, conçu pour s\'intégrer naturellement dans le quotidien de votre enfant, afin de construire un parcours bilingue et biculturel complet.'
              : 'A 100% online program, complementary to French schooling, designed to integrate naturally into your child\'s daily routine, in order to build a complete bilingual and bicultural pathway.'}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Intro SEO */}
      <section className="py-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#14274B] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'À OFA France, chaque cours est dispensé en direct via Zoom par des enseignants américains certifiés. Les classes se tiennent en petits groupes de 4 à 8 élèves, garantissant une attention individualisée et une participation active de chaque enfant. Les sessions sont programmées en dehors des heures de l\'école française — en fin de journée, le mercredi et le samedi — pour s\'intégrer naturellement dans l\'emploi du temps familial.'
              : 'At OFA France, every class is taught live via Zoom by certified American teachers. Classes are held in small groups of 4 to 8 students, ensuring individualized attention and active participation from each child. Sessions are scheduled outside French school hours — in the evening, on Wednesdays and Saturdays — to naturally fit into the family schedule.'}
          </p>
          <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Le suivi académique est assuré tout au long de l\'année grâce à des bulletins semestriels et des rapports de progression envoyés aux parents. Notre plateforme de e-learning est Canvas. C\'est un véritable parcours scolaire structuré, pas un simple cours d\'anglais.'
              : 'Academic monitoring is maintained throughout the year through semester reports and progress updates sent to parents. Our e-learning platform is Canvas. This is a truly structured academic pathway, not just an English class.'}
          </p>
        </div>
      </section>

      {/* Key Facts */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {keyFacts.map((fact, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.05 * index }}
                className="bg-[#E7EAEF] p-6 text-center"
              >
                <fact.icon className="w-8 h-8 text-[#2D78F6] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[#14274B]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                  {fact.value}
                </p>
                <p className="text-sm font-medium text-[#2D78F6] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {fact.label}
                </p>
                <p className="text-xs text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {fact.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* A typical day */}
      <section className="py-16 bg-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-light text-[#14274B] mb-10"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Une Journée Type' : 'A Typical Day'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                data-testid={`how-step-${index}`}
                className="bg-white p-6 border-t-4"
                style={{ borderColor: ['#66CA9D', '#2D78F6', '#F8CD46', '#14274B'][index] }}
              >
                <span className="text-4xl font-light text-[#E7EAEF]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                  {step.num}
                </span>
                <h3
                  className="text-lg font-semibold text-[#14274B] mt-2 mb-2"
                  style={{ fontFamily: 'Gabarito, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p className="text-sm text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.cta.title}
          </h2>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="how-cta"
              size="lg"
              className="bg-white text-[#14274B] hover:bg-[#14274B] hover:text-white px-8 py-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.cta.primary}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
